import DriverChangeLog, { IDriverChange } from "./DriverChangeLog";
import Api from "../services/api";
import { DriveStore } from "../store/reducers";
import { extend } from "../utils/vanilla";
import { DriverChangesActions } from "../store/driverChanges/actions";
import { dateTimeComparator } from "../utils/date";

/**
 * Helper methods for DriverChangeLogs.
 * */
export default class DriverChangeLogs {
    private readonly api: Api;
    private readonly store: DriveStore;

    constructor(api: Api, store: DriveStore) {
        this.api = api;
        this.store = store;
    }

    /**
     * Fetch all DriverChangeLogs from local storage.
     * */
    getLogs = (): DriverChangeLog[] => {
        let apiUser = this.api.getUser();
        let driverChanges: DriverChangeLog[] = [];
        if (apiUser) {
            const storedDriverChanges = this.store.getState().driverChanges[apiUser.id] || [];

            if (storedDriverChanges) {
                for (let entity of storedDriverChanges) {
                    let driverChange = extend(true, {}, entity) as DriverChangeLog;
                    driverChanges.push(driverChange);
                }
            }
        }
        return driverChanges;
    };

    getLastDriverChangeForAllDrivers = (activeUserIds: string[]): DriverChangeLog[] => {
        let driverChangeUsers = Object.keys(this.store.getState().driverChanges);
        if (driverChangeUsers.length) {
            let allDriverChanges: IDriverChange[] = [];
            driverChangeUsers.filter(userId => activeUserIds.indexOf(userId) > -1).forEach(userId => {
                allDriverChanges.push(...this.store.getState().driverChanges[userId]);
            });
            allDriverChanges = allDriverChanges.sort(dateTimeComparator());
            return allDriverChanges.length ? [new DriverChangeLog(extend(true, {}, allDriverChanges[allDriverChanges.length - 1]))] : [];
        }
        return [];
    };

    /**
     * Add or set DriverChangeLogs to local storage. Overwrite old driver changes with new ones.
     * @param newDriverChanges The DriverChangeLogs to add to local storage.
     */
    addLogs = (newDriverChanges: DriverChangeLog[]) => {
        let currentDriverChanges = this.getLogs();
        currentDriverChanges = currentDriverChanges.filter(driverChange => !newDriverChanges.some(newDriverChange => newDriverChange.id && newDriverChange.id === driverChange.id));

        // Don't allow duplicate entries into storage
        currentDriverChanges.push(...newDriverChanges);
        this.setLogs(currentDriverChanges);
    };

    /**
     * Replace the current set of DriverChangeLogs in local storage with the provided set of DriverChangeLogs.
     * @param entities The set of DriverChangeLogs to overwrite local storage with.
     */
    setLogs = (entities: DriverChangeLog[]) => {
        let apiUser = this.api.getUser();
        if (!apiUser) {
            return;
        }
        this.store.dispatch(DriverChangesActions.setDriverChanges({userId: apiUser.id, driverChanges: entities}));
    };

    /**
     * Fetch all DriverChangeLogs from local storage that don't have an ID.
     * */
    getNewLogs = (): DriverChangeLog[] => this.getLogs().filter(log => !log.id);

    /**
     * Purge all DriverChangeLogs that don't have an ID from local storage.
     * */
    purgeNewLogs = () => this.setLogs(this.getLogs().filter(log => !!log.id));
}